import React, { useState, useEffect } from "react";
import * as yup from "yup";
import { PostServiceCaller } from "../../services/serviceCaller";
import { API_URL } from "../../constants";
import { toast } from "react-toastify";
import { setAuthentication } from "../../store/reducers/auth";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { updateNavbarHeading } from "../../constants/layout/store/filterSlice";

// Yup validation schema
const schema = yup.object().shape({
  current_password: yup.string().required("Current password is required"),
  new_password: yup.string().required("New password is required"),
  new_password_confirmation: yup
    .string()
    .oneOf([yup.ref("new_password"), null], "Passwords must match")
    .required("Confirm password is required"),
});

const ResetPassword = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    current_password: "",
    new_password: "",
    new_password_confirmation: "",
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    useEffect(() => {
      dispatch(updateNavbarHeading("Reset Password"))
    }, [])

  const toggleShow = (field) => {
    if (field === "current") setShowCurrentPassword(!showCurrentPassword);
    if (field === "new") setShowNewPassword(!showNewPassword);
    if (field === "confirm") setShowConfirmPassword(!showConfirmPassword);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
      if (errors[name]) {
    setErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[name];
      return newErrors;
    });
  }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await schema.validate(formData, { abortEarly: false });
      setErrors({});

      const response = await PostServiceCaller(
        `${API_URL}change-password`,
        formData,
        "post"
      );

      if (response?.status === 200) {
        navigate("/login");
        localStorage.removeItem("token");
        localStorage.removeItem("user");
        dispatch(setAuthentication(undefined));
        toast.success("Password changed successfully");
        setFormData({
          current_password: "",
          new_password: "",
          new_password_confirmation: "",
        });
      } else {
        toast.error("Something went wrong. Please try again.");
      }
    } catch (err) {
      if (err.name === "ValidationError") {
        const errorObj = {};
        err.inner.forEach((error) => {
          errorObj[error.path] = error.message;
        });
        setErrors(errorObj);
      } else {
        toast.error("Failed to change password");
        console.error(err);
      }
    } finally {
      setIsSubmitting(false);
    }
  };
  const styles = {
    container: {
      minHeight: "65vh",
      backgroundColor: "#fff",
      borderRadius:"8px",
      marginTop:"10px"
    },
    resetCard: {
      width: "100%",
      maxWidth: "400px",
      border: "none",
      borderRadius: "12px",
      backgroundColor: "#ffffff",
      boxShadow: "none",
    },
    cardBody: {
      padding: "20px 15px",
    },
    iconContainer: {
      width: "50px",
      height: "50px",
      backgroundColor: "#e3f2fd",
      borderRadius: "50%",
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      marginBottom: "12px",
    },
    icon: {
      fontSize: "20px",
      color: "#27568B",
    },
    heading: {
      fontSize: "20px",
      fontWeight: "600",
      color: "#333333",
      marginBottom: "8px",
    },
    subheading: {
      fontSize: "12px",
      color: "#666666",
      lineHeight: "1.5",
      marginBottom: "15px",
    },
    formGroup: {
      marginBottom: "10px",
    },
    label: {
      fontSize: "14px",
      fontWeight: "bold",
      color: "#333333",
      marginBottom: "8px",
      display: "block",
    },
    inputWrapper: {
      position: "relative",
    },
    iconInput: {
      position: "absolute",
      left: "16px",
      top: "50%",
      transform: "translateY(-50%)",
      color: "#999999",
      fontSize: "16px",
      zIndex: 2,
    },
    input: {
      width: "100%",
      padding: "12px 16px 12px 45px",
      border: "1px solid #e0e0e0",
      borderRadius: "8px",
      fontSize: "14px",
      backgroundColor: "#ffffff",
      transition: "all 0.2s ease",
      outline: "none",
    },
    inputFocus: {
      borderColor: "#27568B",
    },
    toggleIcon: {
      position: "absolute",
      right: "16px",
      top: "50%",
      transform: "translateY(-50%)",
      color: "#999999",
      cursor: "pointer",
      fontSize: "16px",
      zIndex: 2,
    },
    button: {
      width: "100%",
      backgroundColor: "#27568B",
      border: "none",
      color: "#ffffff",
      padding: "10px 22px",
      borderRadius: "8px",
      fontSize: "16px",
      fontWeight: "500",
      transition: "all 0.2s ease",
      cursor: "pointer",
      marginTop: "8px",
    },
    errorText: {
      color: "#dc3545",
      fontSize: "12px",
      marginTop: "4px",
    },
  };

  return (
      <div className="container-fluid">
        <div
          className="container-fluid d-flex align-items-center justify-content-center"
          style={styles.container}
        >
          <div className="resetCard" style={styles.resetCard}>
            <div className="card-body" style={styles.cardBody}>
              {/* Lock Icon */}
              <div className="text-center">
                <div style={styles.iconContainer}>
                  <i className="bi bi-lock-fill" style={styles.icon}></i>
                </div>
              </div>

              {/* Heading */}
              <h2 className="text-center" style={styles.heading}>
                Reset Your Password
              </h2>

              {/* Subheading */}
              <p className="text-center" style={styles.subheading}>
                Please enter your current password along with your new password
                to update your credentials
              </p>

              {/* Form */}
              <form onSubmit={handleSubmit}>
                {/* Current Password */}
                <div style={styles.formGroup}>
                  <label style={styles.label}>Current Password</label>
                  <div style={styles.inputWrapper}>
                    <i className="bi bi-lock-fill" style={styles.iconInput}></i>
                    <input
                      type={showCurrentPassword ? "text" : "password"}
                      placeholder="Current Password"
                      name="current_password"
                      value={formData.current_password}
                      onChange={handleChange}
                      style={styles.input}
                      onFocus={(e) =>
                        Object.assign(e.target.style, styles.inputFocus)
                      }
                      onBlur={(e) => {
                        e.target.style.borderColor = "#e0e0e0";
                        e.target.style.boxShadow = "none";
                      }}
                    />
                    <i
                      className={`bi ${
                        showCurrentPassword
                          ? "bi-eye-slash-fill"
                          : "bi-eye-fill"
                      }`}
                      onClick={() => toggleShow("current")}
                      style={styles.toggleIcon}
                    ></i>
                  </div>
                  {errors.current_password && (
                    <div style={styles.errorText}>
                      {errors.current_password}
                    </div>
                  )}
                </div>

                {/* New Password */}
                <div style={styles.formGroup}>
                  <label style={styles.label}>New Password</label>
                  <div style={styles.inputWrapper}>
                    <i className="bi bi-lock-fill" style={styles.iconInput}></i>
                    <input
                      type={showNewPassword ? "text" : "password"}
                      placeholder="New Password"
                      name="new_password"
                      value={formData.new_password}
                      onChange={handleChange}
                      style={styles.input}
                      onFocus={(e) =>
                        Object.assign(e.target.style, styles.inputFocus)
                      }
                      onBlur={(e) => {
                        e.target.style.borderColor = "#e0e0e0";
                        e.target.style.boxShadow = "none";
                      }}
                    />
                    <i
                      className={`bi ${
                        showNewPassword ? "bi-eye-slash-fill" : "bi-eye-fill"
                      }`}
                      onClick={() => toggleShow("new")}
                      style={styles.toggleIcon}
                    ></i>
                  </div>
                  {errors.new_password && (
                    <div style={styles.errorText}>{errors.new_password}</div>
                  )}
                </div>

                {/* Confirm Password */}
                <div style={styles.formGroup}>
                  <label style={styles.label}>Confirm Password</label>
                  <div style={styles.inputWrapper}>
                    <i className="bi bi-lock-fill" style={styles.iconInput}></i>
                    <input
                      type={showConfirmPassword ? "text" : "password"}
                      placeholder="Confirm Password"
                      name="new_password_confirmation"
                      value={formData.new_password_confirmation}
                      onChange={handleChange}
                      style={styles.input}
                      onFocus={(e) =>
                        Object.assign(e.target.style, styles.inputFocus)
                      }
                      onBlur={(e) => {
                        e.target.style.borderColor = "#e0e0e0";
                        e.target.style.boxShadow = "none";
                      }}
                    />
                    <i
                      className={`bi ${
                        showConfirmPassword
                          ? "bi-eye-slash-fill"
                          : "bi-eye-fill"
                      }`}
                      onClick={() => toggleShow("confirm")}
                      style={styles.toggleIcon}
                    ></i>
                  </div>
                  {errors.new_password_confirmation && (
                    <div style={styles.errorText}>
                      {errors.new_password_confirmation}
                    </div>
                  )}
                </div>

                <button
                  type="submit"
                  style={styles.button}
                  disabled={isSubmitting}
                  onMouseOver={(e) =>
                    !isSubmitting &&
                    (e.target.style.backgroundColor = "#1565c0")
                  }
                  onMouseOut={(e) =>
                    !isSubmitting &&
                    (e.target.style.backgroundColor = "#1976d2")
                  }
                >
                  {isSubmitting ? "Submitting..." : "Reset Password"}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
  );
};

export default ResetPassword;
